import type { Dayjs } from 'dayjs';

/** Product-owned cluster label presentation shared by both map adapters. */
export const CLUSTER_LABEL_FONT_WEIGHT = '600';
export const CLUSTER_LABEL_FONT_FAMILY = 'Inter, sans-serif';
export const CLUSTER_LABEL_TEXT_SIZES = [12, 14, 16] as const;

/** Closest Mapbox-hosted semibold stack to the dashboard's Inter 600 labels. */
export const MAPBOX_CLUSTER_LABEL_FONT = [
  'Open Sans Semibold',
  'Arial Unicode MS Bold',
] as const;

/** Keep the provider basemap continuous while camera bounds prevent duplicates. */
export const MAPBOX_RENDER_WORLD_COPIES = true;

/**
 * The single-world zoom calculation assumes a flat, north-up viewport.
 * Disable every camera input that can invalidate that geometry.
 */
export const MAPBOX_NORTH_UP_OPTIONS = {
  bearing: 0,
  pitch: 0,
  maxPitch: 0,
  dragRotate: false,
  pitchWithRotate: false,
  touchPitch: false,
} as const;

type MapboxRotationHandlers = {
  touchZoomRotate: { disableRotation: () => void };
  keyboard: { disableRotation: () => void };
};

export const lockMapboxToNorthUp = (map: MapboxRotationHandlers): void => {
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();
};

const MAPBOX_WORLD_SIZE_AT_ZOOM_ZERO_PX = 512;

/**
 * Keep a flat, north-up viewport narrower than one rendered world. Wrapped
 * basemap tiles remain available during panning, but two copies of an
 * operational feature cannot be visible in the same viewport.
 */
export const getSingleWorldMinZoom = (containerWidthPx: number): number => {
  if (!Number.isFinite(containerWidthPx) || containerWidthPx <= 0) return 0;
  return Math.max(
    0,
    Math.log2((containerWidthPx + 1) / MAPBOX_WORLD_SIZE_AT_ZOOM_ZERO_PX)
  );
};

/** Google fitBounds uses its provider default with no explicit outer padding. */
export const DASHBOARD_FIT_BOUNDS_PADDING = 0;

export const formatForecastMarkLabel = (
  startLocalTime: Dayjs,
  forecastHourOffset: number
): string =>
  forecastHourOffset === 0
    ? 'Now'
    : startLocalTime.add(forecastHourOffset, 'hour').format('ddd HH:mm');
